import { useCallback, useEffect, useMemo } from 'react';
import { useConfig, useUpdateDefaults } from './useConfig';
import { notifyPlatformFontFamilyChanged } from '../lib/platform';
import { getDefaultNotificationsEnabled } from '../lib/notifications';
import type { DictationKeyword } from '../lib/api-client/config';

interface Preferences {
	vimMode: boolean;
	compactThread: boolean;
	fontFamily: string;
	smartEdges: boolean;
	threadNavigatorRail: boolean;
	releaseToSend: boolean;
	fullWidthContent: boolean;
	notificationsEnabled: boolean;
	dictationKeywords: DictationKeyword[];
	dictationExcludedProjectKeywords: string[];
	dictationSmartFormatting: boolean;
}

const DEFAULT_FONT_FAMILY = 'IBM Plex Mono';
const DEFAULT_PREFERENCES: Preferences = {
	vimMode: false,
	compactThread: true,
	fontFamily: DEFAULT_FONT_FAMILY,
	smartEdges: true,
	threadNavigatorRail: true,
	releaseToSend: false,
	fullWidthContent: false,
	notificationsEnabled: false,
	dictationKeywords: [],
	dictationExcludedProjectKeywords: [],
	dictationSmartFormatting: true,
};

function cssFontFamily(fontFamily: string): string {
	const trimmed = fontFamily.trim();
	if (!trimmed || trimmed === DEFAULT_FONT_FAMILY) {
		return `"${DEFAULT_FONT_FAMILY}", monospace`;
	}
	return `"${trimmed.replaceAll('"', '\\"')}", "${DEFAULT_FONT_FAMILY}", monospace`;
}

function applyFontFamily(fontFamily: string) {
	if (typeof document === 'undefined') {
		return;
	}
	document.documentElement.style.setProperty(
		'--otto-font-family',
		cssFontFamily(fontFamily),
	);
	document.documentElement.dataset.ottoFontFamily = fontFamily;
	if (notifyPlatformFontFamilyChanged(fontFamily)) {
		return;
	}
	if (window.self !== window.top) {
		window.parent.postMessage(
			{ type: 'otto-font-family-changed', fontFamily },
			'*',
		);
	}
}

export function usePreferences() {
	const { data: config } = useConfig();
	const updateDefaults = useUpdateDefaults();

	const resolvedPreferences = useMemo<Preferences>(
		() => ({
			vimMode: config?.defaults?.vimMode ?? DEFAULT_PREFERENCES.vimMode,
			compactThread:
				config?.defaults?.compactThread ?? DEFAULT_PREFERENCES.compactThread,
			fontFamily:
				config?.defaults?.fontFamily?.trim() || DEFAULT_PREFERENCES.fontFamily,
			smartEdges:
				config?.defaults?.smartEdges ?? DEFAULT_PREFERENCES.smartEdges,
			threadNavigatorRail:
				config?.defaults?.threadNavigatorRail ??
				DEFAULT_PREFERENCES.threadNavigatorRail,
			releaseToSend:
				config?.defaults?.releaseToSend ?? DEFAULT_PREFERENCES.releaseToSend,
			fullWidthContent:
				config?.defaults?.fullWidthContent ??
				DEFAULT_PREFERENCES.fullWidthContent,
			notificationsEnabled:
				config?.defaults?.notificationsEnabled ??
				getDefaultNotificationsEnabled(),
			dictationKeywords:
				config?.defaults?.dictationKeywords ??
				DEFAULT_PREFERENCES.dictationKeywords,
			dictationExcludedProjectKeywords:
				config?.defaults?.dictationExcludedProjectKeywords ??
				DEFAULT_PREFERENCES.dictationExcludedProjectKeywords,
			dictationSmartFormatting:
				config?.defaults?.dictationSmartFormatting ??
				DEFAULT_PREFERENCES.dictationSmartFormatting,
		}),
		[
			config?.defaults?.vimMode,
			config?.defaults?.compactThread,
			config?.defaults?.fontFamily,
			config?.defaults?.smartEdges,
			config?.defaults?.threadNavigatorRail,
			config?.defaults?.releaseToSend,
			config?.defaults?.fullWidthContent,
			config?.defaults?.notificationsEnabled,
			config?.defaults?.dictationKeywords,
			config?.defaults?.dictationExcludedProjectKeywords,
			config?.defaults?.dictationSmartFormatting,
		],
	);

	useEffect(() => {
		if (!config) return;
		applyFontFamily(resolvedPreferences.fontFamily);
	}, [config, resolvedPreferences.fontFamily]);

	const updatePreferences = useCallback(
		(updates: Partial<Preferences>) => {
			const nextUpdates: Partial<Preferences> = {};

			if (updates.vimMode !== undefined) {
				nextUpdates.vimMode = updates.vimMode;
			}
			if (updates.compactThread !== undefined) {
				nextUpdates.compactThread = updates.compactThread;
			}
			if (updates.fontFamily !== undefined) {
				nextUpdates.fontFamily =
					updates.fontFamily.trim() || DEFAULT_FONT_FAMILY;
			}
			if (updates.smartEdges !== undefined) {
				nextUpdates.smartEdges = updates.smartEdges;
			}
			if (updates.threadNavigatorRail !== undefined) {
				nextUpdates.threadNavigatorRail = updates.threadNavigatorRail;
			}
			if (updates.releaseToSend !== undefined) {
				nextUpdates.releaseToSend = updates.releaseToSend;
			}
			if (updates.fullWidthContent !== undefined) {
				nextUpdates.fullWidthContent = updates.fullWidthContent;
			}
			if (updates.notificationsEnabled !== undefined) {
				nextUpdates.notificationsEnabled = updates.notificationsEnabled;
			}
			if (updates.dictationKeywords !== undefined) {
				nextUpdates.dictationKeywords = updates.dictationKeywords;
			}
			if (updates.dictationExcludedProjectKeywords !== undefined) {
				nextUpdates.dictationExcludedProjectKeywords =
					updates.dictationExcludedProjectKeywords;
			}
			if (updates.dictationSmartFormatting !== undefined) {
				nextUpdates.dictationSmartFormatting = updates.dictationSmartFormatting;
			}

			if (Object.keys(nextUpdates).length === 0) {
				return;
			}

			updateDefaults.mutate({ ...nextUpdates, scope: 'global' });
		},
		[updateDefaults],
	);

	return useMemo(
		() => ({ preferences: resolvedPreferences, updatePreferences }),
		[resolvedPreferences, updatePreferences],
	);
}

export type { Preferences };
